import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import OrganizationMultiselect from 'apps/marsinet/src/app/pages/authenticated/surveys/components/organization-multiselect/organization-multiselect';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ColorPicker } from 'primereact/colorpicker';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useToast } from 'apps/marsinet/src/app/context/ToastProvider';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { can, AvailableResourceActions } from '@marsinet/commons';
import './video-tags-buttons.css';

interface VideoTag {
  id: string;
  name: string;
  description?: string;
  color: string;
  videoCount?: number;
  subscriptionCount?: number;
  subscriptions?: Array<{
    o_id: string;
    organizationName: string;
    subscribedAt: Date;
  }>;
  created_at?: string;
}

interface VideoTagsManagementModalProps {
  visible: boolean;
  onHide: () => void;
  onRefreshVideos?: () => any;
}

const VideoTagsManagementModal: React.FC<VideoTagsManagementModalProps> = ({
  visible,
  onHide,
  onRefreshVideos,
}) => {
  const { t } = useTranslation();
  const { role } = useContext(MarsinetContext);
  const { showToast } = useToast();

  // Verificar permisos (no rol) — el backend sigue siendo la fuente de verdad
  const canManageTags =
    !!role && can(role, AvailableResourceActions.videos.manage_tags);

  // ==================== TAB 1: ADMINISTRACIÓN DE TAGS ====================
  const [tags, setTags] = useState<VideoTag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [editingTag, setEditingTag] = useState<VideoTag | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState('3B82F6');
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<VideoTag | null>(null);

  // ==================== TAB 2: SUSCRIPCIONES ====================
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableOrganizations, setAvailableOrganizations] = useState<any[]>(
    []
  );
  const [selectedOrganizations, setSelectedOrganizations] = useState<any[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  const [loading, setLoading] = useState(false);

  // ==================== EFECTOS ====================

  // Cargar tags de administración
  useEffect(() => {
    if (!visible || !canManageTags) return;

    const fetchTags = async () => {
      setLoadingTags(true);
      try {
        const response =
          (await apiServices.videosApi.videosControllerGetTagsWithStats()) as any;
        setTags(response.data.tags || []);
      } catch (error) {
        console.error('Error loading tags:', error);
        showToast(
          'error',
          t('common.error'),
          t('common.error_loading_tags'),
          2000
        );
      } finally {
        setLoadingTags(false);
      }
    };

    fetchTags();
  }, [visible, canManageTags, t]);

  // Cargar organizaciones disponibles (tab 2)
  useEffect(() => {
    if (!visible || !canManageTags) {
      setAvailableOrganizations([]);
      setSelectedOrganizations([]);
      return;
    }

    const loadOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        setSelectedOrganizations([]);
        const response =
          (await apiServices.userApi.usersControllerGetOrganizations()) as any;
        const orgs = response.data || [];
        setAvailableOrganizations(orgs);
      } catch (error) {
        console.error('Error loading organizations:', error);
        showToast(
          'error',
          t('common.error'),
          t('common.error_loading_organizations'),
          2000
        );
      } finally {
        setLoadingOrgs(false);
      }
    };

    loadOrganizations();
  }, [visible, canManageTags, t]);

  // ==================== FUNCIONES TAB 1: ADMINISTRACIÓN ====================

  const reloadTags = async () => {
    const response =
      (await apiServices.videosApi.videosControllerGetTagsWithStats()) as any;
    setTags(response.data.tags || []);
  };

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormColor('3B82F6');
    setSelectedOrgIds([]);
    setEditingTag(null);
  };

  const isTagFormValid =
    formName.trim().length > 0 && formDescription.trim().length > 0;

  const isSaveTagDisabled = loading || !isTagFormValid;

  const handleCreateTag = async () => {
    if (!formName.trim()) {
      showToast(
        'warn',
        t('common.validation'),
        t('common.tag_name_required'),
        2000
      );
      return;
    }

    if (!formDescription.trim()) {
      showToast(
        'warn',
        t('common.validation'),
        t('common.tag_description_required'),
        2000
      );
      return;
    }

    try {
      setLoading(true);
      await apiServices.videosApi.videosControllerCreateTag(
        formName,
        formDescription || undefined,
        `#${formColor}`
      );

      //Check for tags, same as in edit
      if (selectedOrgIds.length > 0) {
        await apiServices.videosApi.videosControllerSubscribeOrgsToTags(
          selectedOrgIds.join(','),
          formName
        );
      }

      showToast(
        'success',
        t('common.success'),
        t('common.tag_created', { name: formName }),
        2000
      );

      resetForm();

      // Recargar tags — esto también refresca las listas derivadas
      await reloadTags();
      onRefreshVideos?.();
    } catch (error) {
      console.error('Error creating tag:', error);
      showToast(
        'error',
        t('common.error'),
        t('common.error_creating_tag'),
        2000
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditTag = (tag: VideoTag) => {
    setEditingTag(tag);
    setFormName(tag.name);
    setFormDescription(tag.description || '');
    setFormColor(tag.color?.replace('#', '') || '3B82F6');
    const currentOrgIds = tag.subscriptions?.map((sub) => sub.o_id) || [];
    setSelectedOrgIds(currentOrgIds);
  };

  const handleUpdateTag = async () => {
    if (!editingTag) return;

    if (!formName.trim()) {
      showToast(
        'warn',
        t('common.validation'),
        t('common.tag_name_required'),
        2000
      );
      return;
    }

    if (!formDescription.trim()) {
      showToast(
        'warn',
        t('common.validation'),
        t('common.tag_description_required'),
        2000
      );
      return;
    }

    try {
      setLoading(true);

      const previousOrgIds =
        editingTag.subscriptions?.map((sub) => sub.o_id) || [];
      const orgsToAdd = selectedOrgIds.filter(
        (id) => !previousOrgIds.includes(id)
      );
      const orgsToRemove = previousOrgIds.filter(
        (id) => !selectedOrgIds.includes(id)
      );

      // Las suscripciones se resuelven ANTES del rename, usando el nombre
      // antiguo, que es el que existe en el catálogo en este momento.
      // Al revés, un rename seguido de subscribe con el nombre viejo crearía
      // un tag fantasma con ese nombre y suscribiría las orgs a él.
      if (orgsToAdd.length > 0) {
        await apiServices.videosApi.videosControllerSubscribeOrgsToTags(
          orgsToAdd.join(','),
          editingTag.name
        );
      }

      if (orgsToRemove.length > 0) {
        await apiServices.videosApi.videosControllerUnsubscribeOrgsFromTags(
          orgsToRemove.join(','),
          editingTag.name
        );
      }

      // El rename es seguro: las suscripciones y las asignaciones a videos
      // referencian el tag por id, así que siguen el cambio solas.
      await apiServices.videosApi.videosControllerUpdateTag(
        editingTag.id,
        formName,
        formDescription || undefined,
        `#${formColor}`
      );

      showToast('success', t('common.success'), t('common.tag_updated'), 2000);

      resetForm();

      await reloadTags();
      onRefreshVideos?.();
    } catch (error) {
      console.error('Error updating tag:', error);
      showToast(
        'error',
        t('common.error'),
        t('common.error_updating_tag'),
        2000
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = (tag: VideoTag) => {
    setTagToDelete(tag);
    setShowDeleteConfirm(true);
  };

  const performDeleteTag = async () => {
    if (!tagToDelete) return;

    try {
      setLoading(true);
      await apiServices.videosApi.videosControllerDeleteTag(tagToDelete.id);

      showToast(
        'success',
        t('common.success'),
        t('common.tag_deleted', { name: tagToDelete.name }),
        2000
      );

      setShowDeleteConfirm(false);
      setTagToDelete(null);

      await reloadTags();
      onRefreshVideos?.();
    } catch (error) {
      console.error('Error deleting tag:', error);
      showToast(
        'error',
        t('common.error'),
        t('common.error_deleting_tag'),
        2000
      );
    } finally {
      setLoading(false);
    }
  };

  // ==================== TEMPLATES ====================

  const colorTemplate = (rowData: VideoTag) => (
    <div className="flex items-center gap-2">
      <div
        style={{
          width: '24px',
          height: '24px',
          borderRadius: '4px',
          backgroundColor: rowData.color || '#3B82F6',
        }}
      />
      <span className="text-xs text-gray-600">{rowData.color}</span>
    </div>
  );

  const statsTemplate = (rowData: VideoTag) => {
    const subscriptionCount = rowData.subscriptions?.length || 0;

    return (
      <div className="flex gap-6 text-sm">
        <div>
          <span className="text-gray-600">
            {t('common.videos_count_label')}{' '}
          </span>
          <Tag value={rowData.videoCount?.toString() || '0'} severity="info" />
        </div>
        <div>
          <span className="text-gray-600">
            {t('common.organizations_count_label')}{' '}
          </span>
          <Tag value={subscriptionCount.toString()} severity="success" />
        </div>
      </div>
    );
  };

  const actionTemplate = (rowData: VideoTag) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        rounded
        text
        severity="info"
        onClick={() => handleEditTag(rowData)}
        disabled={loading}
        className="p-button-sm"
      />
      <Button
        icon="pi pi-trash"
        rounded
        text
        severity="danger"
        onClick={() => handleDeleteTag(rowData)}
        disabled={loading}
        className="p-button-sm"
      />
    </div>
  );

  const tagItemTemplate = (option: string) => {
    const isSelected = selectedTags.includes(option);
    return (
      <div className="flex align-items-center gap-2">
        <span>{option}</span>
        {isSelected && (
          <Tag
            severity="success"
            value={t('common.selected')}
            className="ml-auto"
          />
        )}
      </div>
    );
  };

  const isSubscribeDisabled =
    loading ||
    loadingOrgs ||
    loadingTags ||
    selectedOrganizations.length === 0 ||
    selectedTags.length === 0;

  // ==================== RENDER ====================

  return (
    <>
      <Dialog
        visible={visible}
        onHide={onHide}
        header={t('common.tags_management')}
        modal
        maximizable
        blockScroll
        style={{ width: '90vw', height: '90vh' }}
        className="p-dialog-lg"
      >
        {/*
          Las etiquetas son una funcionalidad de gestión: todas las pestañas
          dependen de endpoints protegidos por manage_tags. Un usuario final
          no debe ver ninguna.
        */}
        {!canManageTags ? (
          <div className="bg-red-50 border border-red-200 rounded p-4">
            <p className="text-sm text-red-900 font-medium">
              {t('common.access_denied')}
            </p>
            <p className="text-sm text-red-800 mt-2">
              {t('common.no_permission')}
            </p>
          </div>
        ) : (

          <div className="space-y-6">
            {/* Create/Edit Form */}
            <div className="bg-blue-50 border border-blue-200 px-6 pb-6 pt-3 rounded-lg">
              <h3 className="font-semibold mb-4 text-blue-900">
                {editingTag ? t('common.edit_tag') : t('common.tag_data')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t('common.tag_name')} *
                  </label>
                  <InputText
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder={t('common.tag_name_placeholder')}
                    disabled={loading}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t('common.color')}
                  </label>
                  <div className="flex gap-2 items-center">
                    <ColorPicker
                      value={formColor}
                      onChange={(e) => {
                        const color =
                          typeof e.value === 'string' ? e.value : formColor;
                        setFormColor(color.replace('#', ''));
                      }}
                      disabled={loading}
                      format="hex"
                    />
                    <span className="text-xs text-gray-600">
                      #{formColor}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <label className="text-sm font-medium text-gray-700">
                  {t('common.description')} *
                </label>
                <InputTextarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder={t('common.tag_description_placeholder')}
                  disabled={loading}
                  rows={2}
                  className="w-full"
                />
              </div>

              <div className="space-y-2 mb-4">
                <label className="text-sm font-medium text-gray-700">
                  {t('common.subscribed_organizations')}
                </label>
                <OrganizationMultiselect
                  organizations={availableOrganizations}
                  value={selectedOrgIds}
                  onChange={setSelectedOrgIds}
                  placeholder={t('common.select_organizations')}
                  maxSelectedLabels={3}
                  className="w-full"
                />
                <small className="text-gray-500">
                  {t('common.organizations_selected_count', {
                    selected: selectedOrgIds.length,
                  })}
                </small>
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  label={t('common.cancel')}
                  outlined
                  onClick={() => {
                    resetForm();
                  }}
                  disabled={loading}
                  className="p-button-sm"
                />
                {editingTag ? (
                  <Button
                    label={t('common.update_tag')}
                    onClick={handleUpdateTag}
                    loading={loading}
                    disabled={isSaveTagDisabled}
                    className="p-button-sm video-tags-btn"
                  />
                ) : (
                  <Button
                    label={t('common.save_tag')}
                    onClick={handleCreateTag}
                    loading={loading}
                    disabled={isSaveTagDisabled}
                    className="p-button-sm video-tags-btn"
                  />
                )}
              </div>
            </div>

            {/* Tags Table */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900">
                  {t('common.existing_tags', { total: tags.length })}
                </h3>
              </div>

              {loadingTags ? (
                <div className="flex justify-center p-6">
                  <ProgressSpinner
                    style={{ width: '50px', height: '50px' }}
                    strokeWidth="4"
                  />
                </div>
              ) : (
                <DataTable
                  value={tags}
                  loading={loading}
                  responsiveLayout="scroll"
                  size="small"
                  className="p-datatable-sm"
                  emptyMessage={t('common.no_tags_created')}
                >
                  <Column
                    field="name"
                    header={t('common.name')}
                    style={{ width: '20%' }}
                  />
                  <Column
                    field="color"
                    header={t('common.color')}
                    body={colorTemplate}
                    style={{ width: '15%' }}
                  />
                  <Column
                    field="description"
                    header={t('common.description')}
                    body={(rowData: VideoTag) => (
                      <span className="text-sm text-gray-600">
                        {rowData.description || '-'}
                      </span>
                    )}
                    style={{ width: '35%' }}
                  />
                  <Column
                    header={t('common.stats')}
                    body={statsTemplate}
                    style={{ width: '20%' }}
                  />
                  <Column
                    header={t('common.actions')}
                    body={actionTemplate}
                    style={{ width: '10%', textAlign: 'center' }}
                  />
                </DataTable>
              )}
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        visible={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setTagToDelete(null);
        }}
        message={t('common.confirm_delete_tag', {
          name: tagToDelete?.name,
          videos: tagToDelete?.videoCount || 0,
          organizations: tagToDelete?.subscriptions?.length || 0,
        })}
        header={t('common.confirm_delete')}
        icon="pi pi-exclamation-triangle"
        accept={performDeleteTag}
        reject={() => {
          setShowDeleteConfirm(false);
          setTagToDelete(null);
        }}
      />
    </>
  );
};

export default VideoTagsManagementModal;